import {
  loadGradingSessionSnapshot,
  saveGradingSessionSnapshot,
  snapshotMatchesAssignment,
  type GradingSessionSnapshot,
} from '../trust/gradingSessionSnapshot';
import type { TrustAutosaveStatus } from '../trust/trustAutosaveTypes';
import { safeParseJson } from '../../utils/safeParseJson';
import type { GradedWork, Student } from '../../types';

export type { GradingSessionSnapshot };
export type HubTrustAutosaveStatus = TrustAutosaveStatus;

export type GradeHubProgress = {
  graded: number;
  total: number;
  draftCount: number;
  finalCount: number;
};

export type GradeHubSession = {
  courseId: string;
  assignmentId: string;
  courseName: string;
  assignmentTitle: string;
  studentId?: string;
  studentName?: string;
  progress: GradeHubProgress;
  savedAt: number;
};

const GRADE_HUB_SESSION_KEY = 'dg_grade_hub_session_v1';

export type GradeViewerSession = {
  pageIndex: number;
  zoomPercent: number;
  fitWidth: boolean;
};

const DEFAULT_VIEWER: GradeViewerSession = {
  pageIndex: 0,
  zoomPercent: 100,
  fitWidth: false,
};

/** Grading snapshot for a specific class + assignment. */
export function loadGradeHubSession(
  courseId: string | undefined,
  assignmentId: string | undefined
): GradingSessionSnapshot | null {
  const snap = loadGradingSessionSnapshot();
  return snapshotMatchesAssignment(snap, courseId, assignmentId) ? snap : null;
}

/** Latest hub meta for resume chip (any assignment). */
export function loadGradeHubSessionMeta(): GradeHubSession | null {
  const parsed = safeParseJson<unknown>(
    typeof window !== 'undefined' ? localStorage.getItem(GRADE_HUB_SESSION_KEY) : null,
    null
  );
  if (!parsed || typeof parsed !== 'object') return null;
  const o = parsed as GradeHubSession;
  if (typeof o.courseId !== 'string' || typeof o.assignmentId !== 'string') return null;
  return o;
}

export function loadGradeViewerSession(
  courseId: string | undefined,
  assignmentId: string | undefined
): GradeViewerSession {
  const snap = loadGradeHubSession(courseId, assignmentId);
  if (!snap) return DEFAULT_VIEWER;
  return {
    pageIndex: snap.viewerPageIndex ?? 0,
    zoomPercent: snap.viewerZoomPercent ?? 100,
    fitWidth: snap.viewerFitWidth ?? false,
  };
}

export function persistGradeHubSession(input: Omit<GradingSessionSnapshot, 'savedAt'>): void {
  saveGradingSessionSnapshot(input);
}

export {
  GRADING_SESSION_SNAPSHOT_KEY,
  clearGradingSessionSnapshot,
} from '../trust/gradingSessionSnapshot';

export function computeGradeHubProgress(input: {
  students: Student[];
  history: GradedWork[];
  gradedWorks: GradedWork[];
  courseId: string;
  assignmentId: string;
  batchStudentIds?: Set<string>;
}): GradeHubProgress {
  const { students, history, gradedWorks, courseId, assignmentId, batchStudentIds } = input;
  const roster =
    batchStudentIds && batchStudentIds.size > 0
      ? students.filter((s) => batchStudentIds.has(s.id))
      : students;
  const total = roster.length;
  const inScope = (w: GradedWork) => w.courseId === courseId && w.assignmentId === assignmentId;
  const gradedIds = new Set(
    [...history, ...gradedWorks]
      .filter(inScope)
      .map((w) => w.studentId)
      .filter(Boolean)
  );
  const graded = roster.filter((s) => gradedIds.has(s.id)).length;
  const draftCount = gradedWorks.filter((w) => inScope(w) && w.status !== 'synced').length;
  const finalCount = gradedWorks.filter((w) => inScope(w) && w.status === 'synced').length;
  return { graded, total, draftCount, finalCount };
}

export function saveGradeHubSession(input: Omit<GradeHubSession, 'savedAt'>): void {
  if (typeof window === 'undefined') return;
  try {
    const payload: GradeHubSession = { ...input, savedAt: Date.now() };
    localStorage.setItem(GRADE_HUB_SESSION_KEY, JSON.stringify(payload));
  } catch {
    /* ignore quota / private mode */
  }
}

export function deriveHubTrustStatus(input: {
  isOnline: boolean;
  syncStatus: 'idle' | 'ok' | 'error';
  pendingDraftCount: number;
}): HubTrustAutosaveStatus {
  if (!input.isOnline) return 'offline';
  if (input.syncStatus === 'error') return 'error';
  if (input.pendingDraftCount > 0) return 'saved';
  return input.syncStatus === 'ok' ? 'saved' : 'idle';
}

export function findNextUngradedStudent(input: {
  students: Student[];
  history: GradedWork[];
  courseId: string;
  assignmentId: string;
  batchOrder?: string[];
  batchStudentIds?: Set<string>;
}): Student | null {
  const roster =
    input.batchStudentIds && input.batchStudentIds.size > 0
      ? input.students.filter((s) => input.batchStudentIds!.has(s.id))
      : input.students;
  const order =
    input.batchOrder && input.batchOrder.length > 0
      ? input.batchOrder
          .map((id) => roster.find((s) => s.id === id))
          .filter((s): s is Student => !!s)
      : roster;
  const graded = new Set(
    input.history
      .filter((w) => w.courseId === input.courseId && w.assignmentId === input.assignmentId)
      .map((w) => w.studentId)
  );
  return order.find((s) => !graded.has(s.id)) ?? null;
}
